// A searchable, paginated selector (S1-17 review #8) over a cursor-paged list: the options are
// every page loaded so far; a search narrows them, and while it matches nothing and the list
// has more pages, the next page loads (following `next_cursor`, one page at a time) until the
// search matches or the list ends. "Load more" fetches the next page on demand. The chosen
// option stays listed while a search hides the others. Built on `@companyos/ui` primitives.
import {
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
} from "@companyos/ui";
import { useEffect, useState } from "react";

export type PickerOption = { id: string; label: string };

export function PagedPicker({
  id,
  label,
  value,
  onChange,
  options,
  hasMore,
  loading,
  onMore,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (id: string) => void;
  options: readonly PickerOption[];
  hasMore: boolean;
  loading: boolean;
  onMore: () => void;
}) {
  const [search, setSearch] = useState("");
  const term = search.trim().toLowerCase();
  const matches = term ? options.filter((o) => o.label.toLowerCase().includes(term)) : options;
  const chosen = options.find((o) => o.id === value);
  const shown = chosen && !matches.includes(chosen) ? [chosen, ...matches] : matches;
  const starved = !!term && matches.length === 0;
  useEffect(() => {
    // Searching past the loaded pages: follow the cursor until something matches.
    if (starved && hasMore && !loading) onMore();
  }, [starved, hasMore, loading, onMore]);
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        type="search"
        aria-label={`Search ${label.toLowerCase()}`}
        placeholder="Search…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {shown.map((o) => (
          <NativeSelectOption key={o.id} value={o.id}>
            {o.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <FieldDescription className="flex flex-wrap items-center gap-2">
        <span data-slot="picker-count">
          {starved && !hasMore
            ? "No match."
            : `${options.length} loaded${hasMore ? ", more available" : ""}.`}
        </span>
        {hasMore ? (
          <Button type="button" size="sm" variant="ghost" disabled={loading} onClick={onMore}>
            {loading ? "Loading…" : "Load more"}
          </Button>
        ) : null}
      </FieldDescription>
    </Field>
  );
}
